import { useState } from "react";
import { useTranslation } from "react-i18next";
import { mixpanelTrackInteraction } from "utils/tracking/mixpanel";
import {
  AutocompleteInput,
  type AutocompleteInputProps,
} from "../autocomplete-input";
import { Row } from "../styles";
import { type AddressFields } from "../utils";
import { ApartmentInput } from "./styles";

export interface SimpleAddressFieldsProps {
  initialValue: AddressFields | null;
  onChange: (address: AddressFields | null) => void;
  onFullAddressTriggerClick: () => void;
  showApartmentField?: boolean;
  stateRestriction?: AutocompleteInputProps["stateRestriction"];
}

export function SimpleAddressFields({
  initialValue,
  onChange,
  onFullAddressTriggerClick,
  showApartmentField = true,
  stateRestriction,
}: SimpleAddressFieldsProps) {
  const { t } = useTranslation("components", {
    keyPrefix: "addressFields",
  });

  const [addressFields, setAddressFields] = useState(initialValue);
  const [apartment, setApartment] = useState(initialValue?.apartment ?? "");
  const [inputValue, setInputValue] = useState("");

  return (
    <Row>
      <AutocompleteInput
        clearOnBlur
        inputValue={inputValue}
        label={t("autocomplete")}
        name="address"
        onChange={(address) => {
          const addressFields: AddressFields | null = address && {
            googlePlacesId: address.googlePlacesId,
            label: address.label,
          };

          setAddressFields(addressFields);
          setInputValue(addressFields?.label ?? "");

          if (addressFields) {
            onChange({ ...addressFields, apartment });
            mixpanelTrackInteraction("select home address from autocomplete", {
              component_name: "address",
              interaction_region: "home address section",
              interaction_type: "text",
              name_of_page: "checkout",
            });
          } else {
            onChange(null);
          }
        }}
        onFocus={() => {
          mixpanelTrackInteraction("click home address field", {
            component_name: "address",
            interaction_region: "home address section",
            interaction_type: "text",
            name_of_page: "checkout",
          });
        }}
        onFullAddressTriggerClick={onFullAddressTriggerClick}
        onInputChange={(value) => {
          setAddressFields(null);
          setInputValue(value);
          onChange(null);
        }}
        stateRestriction={stateRestriction}
        value={addressFields}
        variant="simple"
      />

      {showApartmentField && (
        <ApartmentInput
          inputProps={{
            "aria-label": t("apartment.ariaLabel"),
            enterKeyHint: "go",
          }}
          label={t("apartment.label")}
          name="apartment"
          onChange={(event) => {
            const apartment = event.target.value.slice(0, 7);
            setApartment(apartment);
            onChange(addressFields && { ...addressFields, apartment });
          }}
          value={apartment}
          variant="simple"
        />
      )}
    </Row>
  );
}
